import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { capitalize, isMobileUA } from 'dpl/shared/utils';
import { GENDERS } from 'dpl/shared/constants/breeder_app';
import { BREED_HEALTH_TESTING_GUIDE_PATH } from 'dpl/shared/constants/urls';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { dogResourceDefinition } from 'dpl/dog_modals/utils/queryDefinitions';
import FixedTooltipPortal from 'dpl/common/tooltip/components/FixedTooltipPortal';
import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import Icon from 'dpl/common/components/Icon';
import Badge from 'dpl/common/design_system/Badge';

import ParentModalGallery from './ParentModalGallery';

const IS_MOBILE_UA = isMobileUA();

function ParentDogContent({ dogId }) {
  const parentData = useResourceQuery(dogResourceDefinition(dogId));

  const {
    isLoading,
    value: { data: parent }
  } = parentData;

  const parentTitle = parent.gender === GENDERS.FEMALE ? 'mom' : 'dad';

  const parentGallery = useMemo(() => {
    const photos = [...(parent.photographs || [])];
    if (parent.profile_photo_url) {
      photos.unshift({
        id: parent.profile_photo_id,
        url: parent.profile_photo_url,
        type: 'Photograph'
      });
    }

    return photos;
  }, [parent.photographs]);

  const details = [
    ['Registered name', parent.registered_name],
    ['Breed', parent.breed_name],
    ['Gender', capitalize(parent.gender || '')],
    ['Color', parent.color],
    ['Generation', parent.generation],
    ['Age', parent.age_description_v2],
    ['Size', parent.formatted_weight],
    ['Sire', parent.sire],
    ['Dam', parent.dam]
  ].filter(([, value]) => Boolean(value));

  const dogHealthTestResults = useMemo(
    () =>
      (parent.health_test_results || []).filter(
        htr => (htr.health_test_result_documents_attributes || []).length !== 0
      ),
    [parent]
  );

  const healthGuideLink = (
    <a
      className="underline"
      target="_blank"
      rel="noreferrer noopener"
      href={`/breeds/${parent.breed_slug}${BREED_HEALTH_TESTING_GUIDE_PATH}`}
    >
      Learn more
    </a>
  );

  const hasPublicHealthTests =
    dogHealthTestResults.length > 0 && parent.health_test_results_public;

  return (
    <LoadingWrapper isLoading={isLoading} className="transform-center-all">
      <h4 className="font-24 md:font-32 lh-title">
        {parent.name}, {parentTitle}
      </h4>
      <ParentModalGallery gallery={parentGallery} />
      <div className="flex flex-column gap-24 md:gap-32 mt6 mt8-md">
        {/* parent info */}
        <p className="font-16 md:font-18 secondary lh-body">
          {parent.description || parent.placeholder_description}
        </p>

        <ul
          className={classnames('gap-8', {
            'grid grid-cols-2': IS_MOBILE_UA,
            'flex flex-wrap': !IS_MOBILE_UA
          })}
        >
          {details.map(
            ([label, value], idx, arr) =>
              Boolean(value) && (
                <li
                  key={idx}
                  className={classnames(
                    'bg-gray-100 br2 ph4 pv4 ph5-md pv5-md',
                    {
                      'col-span-2':
                        IS_MOBILE_UA &&
                        idx === arr.length - 1 &&
                        arr.length % 2 !== 0
                    }
                  )}
                >
                  <p className="fw-medium font-12 md:font-14 mb1">{label}</p>
                  <p className="font-14 md:font-16 secondary">{value}</p>
                </li>
              )
          )}
        </ul>

        {/* health tests */}
        <div className="flex flex-column gap-16">
          <div className="flex items-center gap-16">
            <h4 className="font-24 md:font-32">Health tests</h4>
            {parent.health_tier_title && hasPublicHealthTests && (
              <Badge
                className="lh-body"
                variant="bright"
                color="strollBright"
                size={IS_MOBILE_UA ? 'small' : 'default'}
              >
                {parent.health_tier_title}
              </Badge>
            )}
          </div>
          {dogHealthTestResults.length > 0 &&
          parent.health_test_results_public ? (
            <>
              {parent.health_tier_title && (
                <div className="secondary font-16 md:font-18 lh-body">
                  <p>
                    {parent.name} meets Good Dog&apos;s health testing
                    requirements for the {parent.health_tier_title} level.
                  </p>
                  {healthGuideLink}
                </div>
              )}
              {dogHealthTestResults.map(htr => (
                <div className="font-14 md:font-16 lh-body" key={htr.id}>
                  <p className="fw-medium mb1">{htr.health_test_name}</p>
                  <p>
                    {htr.description}
                    {htr.description &&
                      htr.health_test_result_documents_attributes?.length > 0 &&
                      ' · '}
                    {htr.health_test_result_documents_attributes?.map(
                      (doc, index, array) => (
                        <span key={doc.id}>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="stone-700 underline"
                          >
                            View result
                            {array.length > 1 && ` ${index + 1}`}
                          </a>
                          {index < array.length - 1 && ', '}
                        </span>
                      )
                    )}
                  </p>
                </div>
              ))}
            </>
          ) : (
            <p className="secondary font-16 md:font-18 lh-body">
              The breeder reported meeting Good Dog&apos;s health testing
              requirements but has not shared documentation. Contact the breeder
              to discuss health testing performed for {parent.name}.{' '}
              {healthGuideLink}
            </p>
          )}
        </div>

        {/* pedigree */}
        {parent.pedigree_documents?.length > 0 && (
          <div className="flex flex-column gap-8">
            <h4 className="font-24 md:font-32">Pedigree</h4>
            <p className="secondary font-16 md:font-18 lh-body">
              Learn more about {parent.name}&apos;s history and pedigree.
            </p>
            {parent.pedigree_documents?.map((doc, index, array) => (
              <a
                key={index}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="stone-700 underline font-14 md:font-16 lh-body"
              >
                View pedigree
                {array.length > 1 && ` ${index + 1}`}
              </a>
            ))}
          </div>
        )}
      </div>
    </LoadingWrapper>
  );
}

ParentDogContent.propTypes = {
  dogId: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};

ParentDogContent.defaultProps = {
  dogId: null
};

export default function ParentModal({
  dogId,
  isOpen,
  onClose,
  portalMountRef
}) {
  if (IS_MOBILE_UA) {
    return (
      <FixedTooltipPortal
        isVisible={isOpen}
        style={{ height: '85vh' }}
        onCloseClick={onClose}
        portalMountRef={portalMountRef}
      >
        <div className="ParentMobileBottomSheet pt1 ph1">
          <ParentDogContent dogId={dogId} />
        </div>
      </FixedTooltipPortal>
    );
  }

  if (!IS_MOBILE_UA && isOpen) {
    return (
      <MiniOverlayModal
        maxWidth="900px"
        onCloseClick={onClose}
        contentClassName="bg-white overflow-hidden"
        hideCloseButton
        scrollableContent
      >
        <button
          type="button"
          onClick={onClose}
          className="bg-white secondary font-24 z-max absolute top-0 right-0 br-100 ba b--default padding-v-6 padding-h-6 mt6 mr6"
        >
          <Icon name="fetch-close" height="20px" width="20px" />
        </button>
        <div className="ParentModal overflow-y-auto h-100 pt6 pb8 ph8">
          <ParentDogContent dogId={dogId} />
        </div>
      </MiniOverlayModal>
    );
  }

  return null;
}

ParentModal.propTypes = {
  dogId: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  portalMountRef: PropTypes.object
};

ParentModal.defaultProps = {
  dogId: null,
  isOpen: false,
  portalMountRef: {}
};
